import { gql, GraphQLClient } from 'graphql-request'
import { env, isProd } from '../helpers/env'
import {
  Cardtoken,
  LookupInputType,
  SecurePaymentLookupResponseType,
  RecurringPaymentInput,
  RecurringPaymentResponseType,
} from './types'

const parseJwt = (token: any) => {
  try {
    return JSON.parse(atob(token.split('.')[1]))
  } catch (e) {
    return null
  }
}

const papiHost = `https://papi.tm${isProd ? '' : 'dev'}.is`

export const makeLandsbankinnClient = () => {
  const tokenExchangeUrl = `${papiHost}/user/api/v1/token-exchange`
  const landsbankinnClient = new GraphQLClient(env.lbGraphqlUrl, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Accept-language': 'is-IS',
      'apollographql-client-name': 'toyota-web',
      apiKey: env.lbApiKey,
      // Authorization is set in token exchange
    },
  })

  const getCardToken = async () => {
    const query = gql`
      query SecurePaymentAuthentication {
        viewer {
          secureCardPaymentAuthentication {
            jsonWebToken
          }
        }
      }
    `

    const response = await landsbankinnClient.request<Cardtoken>(query)
    return response
  }

  const getSecurePaymentLookup = async (variables: { data: LookupInputType }) => {
    const query = gql`
      mutation SecureCardPaymentLookup($data: ThreeDSecureLookupInput!) {
        secureCardPaymentLookup(data: $data) {
          threeDSecureLookup {
            cavv
            directoryServerTransactionId
            enrolledStatus
            electronicCommerceIndicator
            paresStatus
            threeDSecureVersion
            transactionId
            accessControlServerUrl
            jwtPayload
          }
        }
      }
    `
    const response = await landsbankinnClient.request<{ secureCardPaymentLookup: SecurePaymentLookupResponseType }>(
      query,
      variables
    )
    return response
  }

  const createRecurringPayment = async (variables: { data: RecurringPaymentInput }) => {
    const query = gql`
      mutation CreateRecurringCardPayment($data: SecureRecurringCardPaymentInput!) {
        createRecurringCardPayment(data: $data) {
          recurringCardPayment {
            maskedCardNumber
            tokenExpiryDate
            tokenId
          }
        }
      }
    `
    const response = await landsbankinnClient.request<{ createRecurringCardPayment: RecurringPaymentResponseType }>(
      query,
      variables
    )
    return response
  }

  const exchangeToken = async () => {
    const authHeader = (landsbankinnClient as any).requestConfig.headers?.authorization
    if (authHeader) {
      const token = authHeader.split(' ')[1]
      const decoded = parseJwt(token)
      const now = Date.now() / 1000
      if (decoded && decoded.exp && decoded.exp > now) {
        // Token is valid and don't need a new one
        return
      }
    }

    await fetch(tokenExchangeUrl, {
      credentials: 'include',
    })
      .then((res) => {
        if (!res.ok) {
          return res.json().catch(() => ({ success: false, message: 'HTTP ' + res.status }))
        }
        return res.json()
      })
      .then((data) => {
        if (data.success) {
          const lbToken = `${data.tokenType} ${data.lbToken}`
          landsbankinnClient.setHeader('authorization', lbToken)
        } else {
          throw new Error('Token exchange failed: ' + JSON.stringify(data))
        }
      })
      .catch((error) => {
        throw new Error('Token exchange error: ' + error)
      })
    return
  }

  return { getCardToken, getSecurePaymentLookup, createRecurringPayment, exchangeToken }
}

// Create a singleton instance
export const lbApi = makeLandsbankinnClient()
